/**
 * imageDiff
 *
 * 「カメラに映っているシーンが変化したか」を判定するためのユーティリティ。
 *
 * 仕組み：
 *   1. video から 64x48 の超低解像度ImageDataを取り出す（比較用）
 *   2. 前フレームのImageDataと、各ピクセルRGBの絶対差分を合計
 *   3. ピクセル数 * 3チャネル * 255 で割って [0, 1] に正規化
 *   4. 0.08（CLAUDE.md の閾値）を超えたら「シーン変化あり」と判定
 *
 * これによりカメラを置いたまま机の上を映しているような状況では
 * Claude API を呼ばずコストを抑える。
 */

/** デフォルトの差分判定用サイズ（小さすぎず大きすぎず、要件3.1の60ms以内に収まる） */
const DIFF_WIDTH = 64;
const DIFF_HEIGHT = 48;

/** デフォルトの「シーン変化」閾値（CLAUDE.md コスト意識セクション準拠） */
export const DEFAULT_DIFF_THRESHOLD = 0.08;

/** 差分計算結果 */
export interface DiffResult {
  /** 0=完全に同一, 1=完全に違う */
  diff: number;
  /** 閾値を超えたかどうか */
  changed: boolean;
}

/**
 * 差分計算用の小さなcanvasを使い回す。
 * 毎回createElementすると割と重いので module-private に保持。
 */
let cachedCanvas: HTMLCanvasElement | null = null;
let cachedCtx: CanvasRenderingContext2D | null = null;

function getCanvas(): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  if (cachedCanvas && cachedCtx) {
    return { canvas: cachedCanvas, ctx: cachedCtx };
  }
  const canvas = document.createElement('canvas');
  canvas.width = DIFF_WIDTH;
  canvas.height = DIFF_HEIGHT;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    throw new Error('差分判定用のCanvas 2Dコンテキストを取得できませんでした。');
  }
  cachedCanvas = canvas;
  cachedCtx = ctx;
  return { canvas, ctx };
}

/**
 * <video> から差分判定用の低解像度ImageDataを取り出す
 *
 * @throws video が再生開始前（width/heightが0）の場合
 */
export function captureLowResImageData(video: HTMLVideoElement): ImageData {
  if (video.videoWidth === 0 || video.videoHeight === 0) {
    throw new Error('カメラ映像がまだ準備できていません。');
  }
  const { ctx } = getCanvas();
  ctx.drawImage(video, 0, 0, DIFF_WIDTH, DIFF_HEIGHT);
  return ctx.getImageData(0, 0, DIFF_WIDTH, DIFF_HEIGHT);
}

/**
 * 2つのImageDataの差分を [0, 1] の正規化値で計算
 *
 * 前フレームが null の場合は「比較対象なし」として diff=1, changed=true を返す
 * （初回スキャン時に必ずAPIを呼びたいため）
 */
export function computeDiff(
  prev: ImageData | null,
  curr: ImageData,
  threshold: number = DEFAULT_DIFF_THRESHOLD,
): DiffResult {
  if (!prev) {
    return { diff: 1, changed: true };
  }

  // サイズが違うImageDataは比較不能（呼び出し側のバグ）
  if (prev.data.length !== curr.data.length) {
    return { diff: 1, changed: true };
  }

  // RGB の絶対差分を合計（alphaチャネルはスキップ）
  let totalDiff = 0;
  const data1 = prev.data;
  const data2 = curr.data;
  const len = data2.length;

  for (let i = 0; i < len; i += 4) {
    totalDiff += Math.abs(data2[i] - data1[i]);
    totalDiff += Math.abs(data2[i + 1] - data1[i + 1]);
    totalDiff += Math.abs(data2[i + 2] - data1[i + 2]);
  }

  // 正規化：(ピクセル数 * 3チャネル * 255)
  const maxPossibleDiff = (len / 4) * 3 * 255;
  const diff = totalDiff / maxPossibleDiff;

  return {
    diff,
    changed: diff > threshold,
  };
}
